import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from './baseQuery';
import { User, UserType } from '../../bo-kit/types';
import {
    GetUserResponse,
    ResetPasswordRequest,
    SendCodeRequest,
    SignUpRequest,
    SignUpResponse,
    SliderResponse,
    VerifyRequest,
} from '../../bo-kit/transport/types';

export enum GatewayApiCacheTags {
    USER = 'User',
}

export type LoginRequest = {
    email: string;
    password: string;
    userType?: UserType;
};

export type LoginResponse = {
    data: {
        user: User;
    };
};

const reducerPath = 'gatewayApi' as const;

export const gatewayApi = createApi({
    refetchOnReconnect: true,
    refetchOnMountOrArgChange: true,
    reducerPath,
    baseQuery,
    tagTypes: [GatewayApiCacheTags.USER],
    endpoints: (build) => ({
        login: build.mutation<LoginResponse, LoginRequest>({
            query: (body) => ({
                url: 'v2/auth/login',
                method: 'POST',
                body: { ...body, email: body.email.toLowerCase() },
            }),
            invalidatesTags: [GatewayApiCacheTags.USER],
        }),
        logout: build.mutation<void, void>({
            query: () => ({
                url: 'v2/auth/logout',
                method: 'POST',
            }),
            invalidatesTags: [GatewayApiCacheTags.USER],
        }),
        getSliderItems: build.query<SliderResponse, string>({
            query: (locale) => ({
                url: `content/cms/sign-in-slide?locale=${locale}&populate[]=slider&populate[]=slider.image`,
            }),
        }),
        getUserProfile: build.query<GetUserResponse, void>({
            query: () => ({
                url: 'user/profile',
            }),
            providesTags: [GatewayApiCacheTags.USER],
        }),
        signUp: build.mutation<SignUpResponse, SignUpRequest>({
            query: (body) => {
                switch (body.userType) {
                    case UserType.Customer:
                        return {
                            url: 'user/profile',
                            method: 'POST',
                            body: {
                                email: body.username.toLowerCase(),
                                password: body.password,
                                confirm: body.confirmPassword,
                                user_type: body.userType,
                                ...(body.referralCode && { referal_code: body.referralCode }),
                            },
                        };
                    case UserType.Merchant:
                        return {
                            url: 'mc/merchant-issuer',
                            method: 'POST',
                            body: {
                                email: body.username.toLowerCase(),
                                password: body.password,
                                confirm: body.confirmPassword,
                                ...(body.referralCode && { referal_code: body.referralCode }),
                            },
                        };
                    default:
                        throw new Error('Unexpected userType to register');
                }
            },
        }),
        verify: build.mutation<LoginResponse, VerifyRequest>({
            query: (body) => ({
                url: 'user/profile/verify',
                method: 'POST',
                body: {
                    code: body.confirmationCode.toString(),
                    user_id: body.userId,
                },
            }),
            invalidatesTags: [GatewayApiCacheTags.USER],
        }),
        reSendUserVerify: build.mutation<void, string>({
            query: (userId) => ({
                url: `user/profile/${userId}/resend`,
                method: 'PUT',
                body: {},
            }),
        }),
        resetPasswordSendCode: build.mutation<void, SendCodeRequest>({
            query: (body) => ({
                url: 'v2/auth/reset-password/send-code',
                method: 'POST',
                body,
            }),
        }),
        resetPassword: build.mutation<void, ResetPasswordRequest>({
            query: (body) => ({
                url: 'v2/auth/reset-password/confirm',
                method: 'POST',
                body,
            }),
        }),
    }),
});

export const {
    useLazyGetSliderItemsQuery,
    useSignUpMutation,
    useVerifyMutation,
    useReSendUserVerifyMutation,
    useLoginMutation,
    useLogoutMutation,
    useResetPasswordSendCodeMutation,
    useResetPasswordMutation,
    useGetUserProfileQuery,
} = gatewayApi;
